// LLMCostByUserTile.js — top-10 users by LLM cost in the window.
// Data: llm_usage_daily per-user docs (system doc filtered out client-side).
// Resolves uids to display names via point-reads against users/{uid}.

import React, { useMemo } from 'react';
import { Card, CardContent, Typography, Box, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useLLMByUser, useUserNames, toCSV, downloadCSV } from '../../../hooks/useAdminMetrics';

const formatUSD = (n) => `$${n.toFixed(2)}`;

const LLMCostByUserTile = ({ days = 30, limit = 10 }) => {
  const { rows, loading, error } = useLLMByUser(days, limit);
  const uids = useMemo(() => rows.map((r) => r.uid), [rows]);
  const { namesByUid } = useUserNames(uids);

  const labelFor = (uid) => namesByUid[uid] || uid;

  const handleCSV = () => {
    const csvRows = rows.map((r) => ({ ...r, name: labelFor(r.uid) }));
    downloadCSV(
      `llm-cost-by-user-${days}d.csv`,
      toCSV(csvRows, [
        { key: 'name', header: 'name' },
        { key: 'uid', header: 'uid' },
        { key: 'calls', header: 'calls' },
        { key: 'cost_usd', header: 'cost_usd' },
      ]),
    );
  };

  if (error) {
    return (
      <Card><CardContent>
        <Typography color="error">Cost-by-user unavailable: {error.message}</Typography>
      </CardContent></Card>
    );
  }

  const chartData = rows.map((r) => ({ ...r, label: labelFor(r.uid) }));

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" gutterBottom>LLM Cost by User — Top {limit} ({days}d)</Typography>
          <MuiTooltip title="Download CSV (name + full uid)">
            <span>
              <IconButton size="small" onClick={handleCSV} disabled={loading || rows.length === 0}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </MuiTooltip>
        </Box>
        <Box sx={{ width: '100%', height: 320 }}>
          {loading ? (
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No per-user activity in window.</Typography>
          ) : (
            <ResponsiveContainer>
              <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={formatUSD} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="label" width={160} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v) => formatUSD(v)}
                  labelFormatter={(_, payload) => (payload?.[0]?.payload?.uid ?? '')}
                />
                <Bar dataKey="cost_usd" fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Top users ranked by LLM spend in the window. Useful for flagging heavy users and sizing per-user pricing.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default LLMCostByUserTile;
